<script lang="ts" setup>
import './payment.scss';
import { usePurchaseStore } from './../../stores/purchase';
import { useLocalizationStore } from './../../stores/localization';
import { useUserStore } from '@/stores/user';
import { useAlertsStore } from '@/stores/alerts';
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { submitPayment, finishPaySafePayment } from '@/api/client/client_accounts.buy';
import { useRouter, useRoute } from 'vue-router';
import { useMatomo } from '@/composables/useMatomo';
import { configurationPaySafe } from './payment.utils';
import config from '@/config';
import { useModalsStore } from '@/stores/modals';
import { loadExternalScript, signalParent, checkPopup, openTab } from '@/utils';
import filters from '@/filters';
const { toggleModal, closeModal } = useModalsStore();

const { marketing } = useLocalizationStore();
const { updateCart, getCurrentAmount, clearCart, getIssuers, getListProviderIssuers, getBonusAmount } = usePurchaseStore();
const { packages, cart, fees, payment_methods, promo, getBonus, getMethods, getCartList } = storeToRefs(usePurchaseStore());
const { openMessage } = useAlertsStore();
const { isLoggedIn } = useUserStore();
const { trackEvent, trackGoal } = useMatomo();
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const purchase = usePurchaseStore();

const props = defineProps<{
    modal: boolean;
}>();

let promoCredits = ref(0);
let promoCode = ref('');
let promoCounter = ref(0);
let promoAlert = ref({}) as any;
let showDiscount = ref(false);

let paymentMethods = ref([]);
let selectedPayment = ref('');
let paymentDisabled = ref(true);
let issuer = ref(undefined);

watch(() => !isLoggedIn(), async () => {
    router.push({ name: 'grid' });
});

onMounted(load);

function load() {
    if (!window.paysafeScriptLoaded) {
        loadExternalScript('https://hosted.paysafe.com/js/v1/latest/paysafe.min.js').onload = () => {
            window.paysafeScriptLoaded = true;
        };
        //also load checkout for other payment methods
        loadExternalScript('https://hosted.paysafe.com/checkout/v2/paysafe.checkout.min.js').onload = () => {
            window.paysafeScriptLoaded = true;
        };
    } 

    // Payment Failure message for checkout page!        
    window.onmessage = function (e) {
        if (e.data.state === "failure") {
            openMessage({
                content: 'payment.alerts.errorPaymentFailure',
                class: 'error'
            });
            trackEvent('Payment', 'paymentFailure');
            checkPopup();
            router.replace({ path: '/payment/' });
            return;
        }
      
    };
  
    if (route.name === 'paymentfailure') {
        const query = new URLSearchParams(window.location.search);
        if(query.has('info') === true){
            openMessage({
                content: query.get('info')!,
                class: 'error'
            });
            const failureReason = query.get('info') || 'Unknown reason';
            trackEvent('Payment', 'paymentFailure', failureReason);
        } else {
            openMessage({
                content: 'payment.alerts.errorPaymentFailure',
                class: 'error'
            });
            trackEvent('Payment', 'paymentFailure');
        }

        checkPopup();
        

        router.replace({ path: '/payment/' });
    }
}

function editPackages(id: number, type: string) {
    let current = getCurrentAmount(id);

    if (type === 'add') {
        updateCart(id, (current += 1));
        trackEvent('Update Cart', 'Add Item', `ID: ${id}, New Quantity: ${current}`);
    }
    if (type === 'remove') {
        if (current <= 0) {
            return;
        }
        updateCart(id, (current -= 1));
        trackEvent('Update Cart', 'Remove Item', `ID: ${id}, New Quantity: ${current}`);
    }
}

function selectPayment(paymentType: string) {
    selectedPayment.value = paymentType;
    trackEvent('Payment', 'paymentMethodSelected', paymentType);
    paymentDisabled.value = !getListProviderIssuers.includes(selectedPayment.value);
}

function setPromoAlert(message: string) {
    promoAlert.value = { message: message };
    setTimeout(() => {
        promoAlert.value = {};
    }, 2000);
}

function verifyPromo() {
    if (!promoCode.value) {
        return;
    }

    const codesArray = promo!.value!.code!.split(',');
    if (!codesArray.includes(promoCode.value.toUpperCase())) {
        setPromoAlert('payment.alerts.errorIncorrectPromo');
        throw 'Incorrect promo';
    }

    if (promo!.value!.used) {
        setPromoAlert('payment.alerts.errorUsedPromo');
        throw 'Allready used promo';
    }

    promoCredits.value = promo!.value!.credits;
    setPromoAlert('payment.alerts.successCorrectPromo');
}

function promoCheck(){
    if (promoCode.value && promoCounter.value <= 1) {
        promoCounter.value++;
        try {
            verifyPromo();
            promoCounter.value = 0;
        } catch (error) {
            if (promoCounter.value === 1) {
                promoCode.value = '';
                promoCounter.value = 0;
            }
            return;
        }
    }
}

function issuerChange(is: Event){
    issuer.value = (is.target as any).value;
    paymentDisabled.value = issuer.value === 'undefined' ? false : true;
}

async function submitPurchase() {
    promoCheck();

    // Only if payment is paysafe2
    if(selectedPayment.value === 'paysafe2/CREDITCARD'){
        trackEvent('Paysafe', 'Initiate Transaction', `Total: ${total.value}`);
        toggleModal('paysafe');
        //if you want the checkout method use this
        //return paysafeCheckout();
        return;
    }

    trackEvent('Payment', 'requestTransaction', `Total: ${total.value}`);
    paymentDisabled.value = false;

    //dirty fix with paysafe2 maybe backend fix ?
    const { error, result } = await submitPayment(selectedPayment.value.replace('paysafe2', 'paysafe'), total.value, promoCode.value.toUpperCase(), issuer.value);
        
    if (error || !result) {
        paymentDisabled.value = true;
        clearCart();
        // Todo: this.$store.dispatch('errorMessage', 'payment.alerts.errorNoConnection');
        trackEvent('Payment', 'failedTransaction', 'No connection or API Error');
        throw 'No connection to payment provider! or No correct api results';
    }

    if(props.modal){
        await makeTab(result);
    } else {
        await makeForm(result);
    }

    trackEvent('Payment', 'requestPaymentProvider', selectedPayment.value, total.value/100);
    clearCart();
}

async function makeTab(result: any){
    const windowPayment = openTab(result.redirectURL, 'width=830,height=630');   
    paymentDisabled.value = true; 
    const timer = setInterval(async () => { 
        if (windowPayment && windowPayment.closed) {       
            clearInterval(timer);
            closeModal('payment');  
        }
    }, 1000);

    return windowPayment;
}

async function makeForm(result: any){
    const redirForm = document.createElement('form');
    redirForm.setAttribute('method', 'post');
    redirForm.setAttribute('name', 'redirform');
    redirForm.setAttribute('action', result.redirectURL);

    var i, input;
    for (i in result.form) {
        input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', i);
        input.setAttribute('value', (<any>result.form)[i]);
        redirForm.appendChild(input);
    }

    trackEvent('Payment', 'generatedHiddenForm', `Method: ${selectedPayment.value}`);

    document.getElementsByTagName('body')[0].appendChild(redirForm);
    redirForm.submit();

    paymentDisabled.value = true;
    window.location.href = result.redirectURL;
}

// async function paysafeCheckout() {
//     window.scrollTo({ top: 0, behavior: 'smooth' });

//     const closeAfter = 3000;
//     const selectedPayment = payment_methods.value.find((obj: any) => obj.abbr === 'paysafe2/CREDITCARD');
//     const cartList = getCartList.value;
//     if(!selectedPayment || cartList.length < 1){ return }
  
//     const options = configurationPaySafe(total.value, selectedPayment.account_id!, selectedPayment.environment!);
//     paysafe.checkout.setup(selectedPayment.refresh_token, options, async function(instance: any, errorPaysafe: any, result: any) {
//         if (result && result.paymentHandleToken) {
//             const payload = { amount: result.amount, paymentHandleToken: result.paymentHandleToken.toString(), keywords: cartList };
//             const { error } = await finishPaySafePayment(payload);

//             if (error) {
//                 instance.showFailureScreen();
//                 setTimeout(() => { instance.close(); }, closeAfter);
//                 trackEvent('Paysafe', 'Transaction Failure', error.message);
//                 return;
//             }

//             setTimeout(() => { 
//                 instance.close(); 
//                 clearCart();
//                 router.push({ name: 'thankyou' });
//                 trackGoal(1, (total.value/100));
//                 trackEvent('Paysafe', 'Transaction Success');
//                 clearCart();
//             }, closeAfter);

//             instance.showSuccessScreen();

//         } else {       
//             instance.showFailureScreen();
//             setTimeout(() => { instance.close(); }, closeAfter);
//             trackEvent('Paysafe', 'Transaction Failure', errorPaysafe?.message);
//             return;
//         }
//     }, async function (stage: string, expired: boolean) {
//         console.error('Close', stage); 
//         if (stage) {
//             trackEvent('Paysafe', 'Transaction Canceled', `closed at stage: ${stage}`);                
//         }

//         if (expired) {
//             trackEvent('Paysafe', 'Transaction Expired');           
//         }         

//         signalParent();        
//         router.push({ name: 'paymentfailure' });
//     });
// }

const sortPackages = computed(() => {
    let promo = packages.value.some(obj => obj.id === 0);
    let pack = promo ? packages.value.slice() : packages.value.slice().reverse();
    return pack;
});

const blockPurchase = computed(() => {
    let blocked = total.value / 100 > 1099;
    return blocked;
});

const credits = computed(() => {
    return cart.value.reduce((calc, obj) => {
        return calc + obj.quantity * obj.amount;
    }, 0);
});

const total = computed(() => {
    return cart.value.reduce((calc, obj) => {
        return calc + obj.price * obj.amount;
    }, 0);
});

const nextBonus = computed(() => {
    const nextBonus = fees.value.reduce<undefined| any>((result, fee) => {
        if(credits.value < fee.amount && (!result || fee.amount < result.amount)) {
            result = fee;
        }

        return result;
    }, undefined);

    return nextBonus;
});

const canBuy = computed(() => {
    return credits.value === 0 || selectedPayment.value === '' ? false : true;
});
</script>

<template src="./payment.html"></template>